import React, { useState, useMemo } from 'react';
import { getStatusColor, getStatusDisplayName } from '../constants/statusTypes';
import './StatusTimeline.css';

const StatusTimeline = ({ logEntries, metric }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Generate timeline data for the last 90 days
  const timelineData = useMemo(() => {
    const days = 90;
    const today = new Date();
    const timeline = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Find log entry for this date
      const entry = logEntries.find(entry => {
        // Convert both dates to YYYY-MM-DD format for comparison
        const entryDateStr = entry.date; // Already in YYYY-MM-DD format
        const timelineDateStr = date.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
        return entryDateStr === timelineDateStr;
      });

      timeline.push({
        date,
        entry,
        status: entry ? entry.status : null,
        notes: entry ? entry.notes : null
      });
    }

    // Debug logging
    console.log('Timeline data:', timeline.filter(day => day.entry));
    console.log('Log entries:', logEntries);

    return timeline;
  }, [logEntries]);

  // Calculate uptime percentage
  const uptimePercentage = useMemo(() => {
    const daysWithData = timelineData.filter(day => day.status !== null);
    if (daysWithData.length === 0) return '0.0';
    
    const operationalDays = daysWithData.filter(day => day.status === 'operational').length;
    return ((operationalDays / daysWithData.length) * 100).toFixed(1);
  }, [timelineData]);

  // Get today's status
  const todaysStatus = useMemo(() => {
    const today = timelineData[timelineData.length - 1]; // Last day in timeline
    return today?.status || null;
  }, [timelineData]);

  const getStatusBarColor = (status) => {
    if (!status) return '#e5e5e5'; // No data - light gray
    return getStatusColor(status);
  };

  const getStatusBarHeight = (status) => {
    return '100%'; // All bars same height
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateWithDay = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatValue = (entry) => {
    if (!entry || entry.value === undefined || entry.value === null) return '';
    
    if (metric?.type === 'numeric') {
      return `${entry.value} ${metric.unit || ''}`;
    } else {
      return entry.value;
    }
  };

  return (
    <div className="status-timeline">
      <div className="timeline-summary-header">
        <span className="timeline-summary-label">{metric?.name || 'Metric'}</span>
        {todaysStatus && (
          <div className="current-status" style={{ color: getStatusColor(todaysStatus) }}>
            {getStatusDisplayName(todaysStatus)}
          </div>
        )}
      </div>
      
      <div className="timeline-container">
        <div className="timeline-bars">
          {timelineData.map((day, index) => (
            <div
              key={index}
              className="timeline-bar"
              style={{
                backgroundColor: getStatusBarColor(day.status),
                height: getStatusBarHeight(day.status)
              }}
              onMouseEnter={() => {
                console.log('Hovering over bar:', index, timelineData[index]);
                setHoveredIndex(index);
              }}
              onMouseLeave={() => {
                console.log('Leaving bar:', index);
                setHoveredIndex(null);
              }}
            />
          ))}
        </div>
        
        <div className="timeline-labels">
          <span className="timeline-label-start">90 days ago</span>
          <div className="timeline-separator"></div>
          <span className="uptime-percentage">{uptimePercentage}% uptime</span>
          <div className="timeline-separator"></div>
          <span className="timeline-label-end">Today</span>
        </div>

        {/* Tooltip */}
        {hoveredIndex !== null && (
          <div 
            className="timeline-tooltip"
            style={{
              left: `${(hoveredIndex / (timelineData.length - 1)) * 100}%`
            }}
          >
            <div className="tooltip-content">
              <div className="tooltip-date">{formatDateWithDay(timelineData[hoveredIndex].date)}</div>
              {timelineData[hoveredIndex].entry ? (
                <>
                  <div className="tooltip-value">{formatValue(timelineData[hoveredIndex].entry)}</div>
                  <div className="tooltip-status" style={{ color: getStatusColor(timelineData[hoveredIndex].status) }}>
                    {getStatusDisplayName(timelineData[hoveredIndex].status)}
                  </div>
                  {timelineData[hoveredIndex].notes && (
                    <div className="tooltip-notes">{timelineData[hoveredIndex].notes}</div>
                  )}
                </>
              ) : (
                <div className="tooltip-no-data">No data available for this day</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusTimeline;
